/*
 * Copyright Camunda Services GmbH and/or licensed to Camunda Services GmbH under
 * one or more contributor license agreements. See the NOTICE file distributed
 * with this work for additional information regarding copyright ownership.
 * Licensed under the Camunda License 1.0. You may not use this file
 * except in compliance with the Camunda License 1.0.
 */

import { useRef, useEffect, useState } from 'react';
import { Close, Draggable } from '@carbon/icons-react';
import { Button } from '@carbon/react';
import { FocusTrap } from 'focus-trap-react';

import { useDraggableConstrainedPosition, useResizable } from './hooks';
import { DEFAULT_CONSTRAINTS } from './config/constraints';
import { Wrapper, Header, HeaderLeft, HeaderRight, Title, Content, ResizeHandle } from './PropertiesPanelPopup.styled';
import { calculateCenteredPosition } from './util';

/**
 * @typedef {import('react').FocusEvent<HTMLDivElement>} DivFocusEvent
 * @typedef {import('react').CSSProperties} CSSProperties
 */

/**
 * @typedef {Object} SizeConstraints
 * @property {number} minWidth - Minimum width in pixels
 * @property {number} maxWidth - Maximum width in pixels
 * @property {number} minHeight - Minimum height in pixels
 * @property {number} maxHeight - Maximum height in pixels
 */

/**
 * @typedef {Object} Position
 * @property {number} x - X coordinate
 * @property {number} y - Y coordinate
 */

/**
 * @typedef {Object} Size
 * @property {number} width - Width in pixels
 * @property {number} height - Height in pixels
 */

/**
 * @typedef {Object} PropertiesPanelPopupOptions
 * @property {React.ReactNode} [children] - Content to display in the popup
 * @property {string} [title] - Title of the popup
 * @property {() => void} [onClose] - Callback when close button is clicked
 * @property {React.RefObject<HTMLElement>} [returnFocus] - Element to focus when popup closes
 * @property {React.ReactNode} [headerItemsLeft] - Additional content for the left side of header
 * @property {React.ReactNode} [headerItemsRight] - Additional content for the right side of header
 * @property {Position} [initialPosition] - Initial position of the popup
 * @property {Size} [initialSize] - Initial size of the popup
 * @property {SizeConstraints} [sizeConstraints] - Size constraints for resizing
 */

/**
 * A draggable and resizable popup panel component
 * @param {PropertiesPanelPopupOptions} options - Component options
 * @returns {JSX.Element} The rendered popup component
 */
export const PropertiesPanelPopup = (options = {}) => {
  const wrapperRef = useRef(null);
  const headerRef = useRef(null);
  const closeButtonRef = useRef(null);
  const [isFocusTrapped, setIsFocusTrapped] = useState(false);

  const {
    children,
    title,
    onClose,
    returnFocus,
    headerItemsLeft,
    headerItemsRight,
    initialPosition,
    initialSize = { width: 300, height: 200 },
    sizeConstraints = DEFAULT_CONSTRAINTS
  } = options;

  useEffect(() => {
    const handleEscape = (event) => {
      if (event.key === 'Escape' && wrapperRef.current?.contains(document.activeElement)) {
        setIsFocusTrapped(false);
        onClose();
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [onClose]);

  /**
   * Handle focus entering the popup
   * @param {DivFocusEvent} event - The focus event
   */
  const handleFocus = (event) => {
    if (!wrapperRef.current?.contains(event.relatedTarget)) {
      setIsFocusTrapped(true);
    }
  };

  /**
   * Handle focus leaving the popup
   * @param {DivFocusEvent} event - The blur event
   */
  const handleBlur = (event) => {
    if (!wrapperRef.current?.contains(event.relatedTarget)) {
      setIsFocusTrapped(false);
    }
  };

  const { position, isDragging } = useDraggableConstrainedPosition(
    wrapperRef,
    headerRef,
    initialPosition || calculateCenteredPosition(initialSize)
  );
  const { size, isResizing, handleResizeStart } = useResizable(initialSize, sizeConstraints, position);

  /** @type {CSSProperties} */
  const wrapperStyle = {
    transform: `translate(${position.x}px, ${position.y}px)`,
    width: `${size.width}px`,
    height: `${size.height}px`,
    userSelect: isDragging || isResizing ? 'none' : 'auto'
  };

  const handleDeactivate = () => {
    if (returnFocus?.current) {
      returnFocus.current.focus();
    }
  };

  const handleClose = () => {
    setIsFocusTrapped(false);
    onClose();
  };

  return (
    <FocusTrap
      active={isFocusTrapped}
      focusTrapOptions={{
        onActivate: () => setIsFocusTrapped(true),
        onDeactivate: handleDeactivate,
        fallbackFocus: () => closeButtonRef.current,
        clickOutsideDeactivates: true,
        allowOutsideClick: true,
        escapeDeactivates: true,
        returnFocusOnDeactivate: false
      }}
    >
      <Wrapper
        style={wrapperStyle}
        ref={wrapperRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="popup-title"
        onFocus={handleFocus}
        onBlur={handleBlur}
      >
        <Header ref={headerRef}>
          <HeaderLeft>
            <Draggable />
            <Title id="popup-title">{title}</Title>
            {headerItemsLeft}
          </HeaderLeft>
          <HeaderRight>
            {headerItemsRight}
            <Button
              ref={closeButtonRef}
              onClick={handleClose}
              hasIconOnly
              iconDescription="Close"
              renderIcon={Close}
              size="md"
              kind="ghost"
              tooltipPosition="bottom"
              aria-label="Close"
            />
          </HeaderRight>
        </Header>
        <Content>{children || ''}</Content>
        <ResizeHandle onMouseDown={handleResizeStart} />
      </Wrapper>
    </FocusTrap>
  );
};
